import { useEffect, useRef, useState } from "react";

// In-app replacement for window.confirm. Mount <ConfirmDialogHost /> once (App.js),
// then anywhere: `if (!(await confirmDialog({ title, message }))) return;`
let openDialog = null;

export function confirmDialog(options) {
  const opts = typeof options === "string" ? { message: options } : options || {};
  if (!openDialog) {
    console.warn("confirmDialog called before <ConfirmDialogHost /> mounted.");
    return Promise.resolve(false);
  }
  return new Promise((resolve) => openDialog({ ...opts, resolve }));
}

export function ConfirmDialogHost() {
  const [dialog, setDialog] = useState(null);
  const confirmRef = useRef(null);

  useEffect(() => {
    openDialog = (next) => setDialog((prev) => {
      prev?.resolve(false);
      return next;
    });
    return () => { openDialog = null; };
  }, []);

  const close = (result) => {
    setDialog((prev) => {
      prev?.resolve(result);
      return null;
    });
  };

  useEffect(() => {
    if (!dialog) return undefined;
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); close(false); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [dialog]);

  if (!dialog) return null;

  const {
    title = "Are you sure?",
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "danger",
    icon = tone === "danger" ? "ki-trash" : "ki-information-5",
  } = dialog;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1090 }} />
      <div className="modal fade show d-block" role="alertdialog" aria-modal="true" style={{ zIndex: 1095 }} onClick={() => close(false)}>
        <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-content d6m">
            <div className="d6m-head">
              <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
              <div className="min-w-0 flex-grow-1">
                <h3 className="d6m-title text-truncate">{title}</h3>
              </div>
              <button type="button" className="d6m-x" onClick={() => close(false)} aria-label="Close">
                <i className="ki-outline ki-cross"></i>
              </button>
            </div>
            {message && (
              <div className="d6m-body">
                <div className="text-gray-700 fs-7" style={{ lineHeight: 1.6 }}>{message}</div>
              </div>
            )}
            <div className="d6m-foot">
              <button type="button" className="btn btn-light d6-mbtn" onClick={() => close(false)}>{cancelLabel}</button>
              <button
                ref={confirmRef}
                type="button"
                className={`btn d6-mbtn ${tone === "danger" ? "btn-danger" : "btn-success"}`}
                onClick={() => close(true)}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
